import React, { useEffect, useRef, useState } from "react";
import useEnveloppes from "../../FinanceV2/hooks/useEnveloppes";
import Loader from "../../../common/Loader";
import TagInput from "../../../common/TagInput";
import EnveloppeCard from "./EnveloppeCard";
import EnveloppeTable from "./EnveloppeTable";
import FinanceurConfigPanel from "./FinanceurConfigPanel";
import styles from "./FinanceurPanel.module.scss";

const initiale = (name) => (name || "?").trim().charAt(0).toUpperCase() || "?";

/**
 * Panneau droit de la page Financeurs : en-tête (nom éditable inline +
 * suppression), bandeau de capacité par enveloppe (lecture) et tableau de
 * gestion des enveloppes. Gère aussi le mode création d'un financeur.
 */
const FinanceurPanel = ({
  financier,
  creating,
  onCreate,
  onRename,
  onModifierContact,
  onModifierAccordFinancement,
  onDelete,
  onEnveloppesChanged,
}) => {
  const uuid = financier?.uuid || null;
  const {
    enveloppes,
    financeurConfig,
    isLoading,
    load,
    creer,
    modifier,
    cloturer,
    modifierEtapes,
    synchroniserTodos,
    modifierEtapesFinanceur,
    synchroniserTodosFinanceur,
  } = useEnveloppes(uuid);

  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [createValue, setCreateValue] = useState("");
  const [busy, setBusy] = useState(false);
  const createInputRef = useRef(null);

  // LS-625 — email par défaut / emails en copie, préremplis dans la modale
  // d'envoi au financeur (FinanceDetailV2).
  const [editingContact, setEditingContact] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [emailsCcValue, setEmailsCcValue] = useState([]);

  // LS-666 — bascule immédiate (pas de mode édition dédié), même principe que
  // basculerTodo côté demande.
  const [busyAccord, setBusyAccord] = useState(false);

  useEffect(() => {
    if (uuid) load();
  }, [uuid, load]);

  // Réinitialise l'édition du nom quand on change de financeur.
  useEffect(() => {
    setEditingName(false);
    setNameValue(financier?.name || "");
  }, [uuid, financier?.name]);

  // Réinitialise l'édition du contact quand on change de financeur.
  useEffect(() => {
    setEditingContact(false);
    setEmailValue(financier?.email || "");
    setEmailsCcValue(financier?.emailsCc || []);
  }, [uuid, financier?.email, financier?.emailsCc]);

  useEffect(() => {
    if (creating && createInputRef.current) createInputRef.current.focus();
  }, [creating]);

  // ── Mode création ──
  if (creating) {
    const submitCreate = async () => {
      const name = createValue.trim();
      if (!name || busy) return;
      setBusy(true);
      await onCreate(name);
      setBusy(false);
      setCreateValue("");
    };
    return (
      <div className={styles.panel}>
        <div className={styles.createBox}>
          <label className={styles.createLabel} htmlFor="new-financeur">
            Nom du nouveau financeur
          </label>
          <div className={styles.createRow}>
            <input
              id="new-financeur"
              ref={createInputRef}
              className={styles.createInput}
              value={createValue}
              onChange={(e) => setCreateValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitCreate()}
              placeholder="ex. BPCE Lease"
            />
            <button
              type="button"
              className={styles.btnSavePill}
              onClick={submitCreate}
              disabled={!createValue.trim() || busy}
            >
              <i className="fa fa-plus" /> Créer
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Aucune sélection ──
  if (!financier) {
    return (
      <div className={styles.panel}>
        <div className={styles.invite}>
          <i className="fa fa-hand-pointer" />
          <span>Sélectionnez un financeur pour gérer ses enveloppes.</span>
        </div>
      </div>
    );
  }

  const actives = enveloppes.filter((e) => e.estActive);
  const hasActiveEnveloppes = actives.length > 0;

  const saveName = async () => {
    const name = nameValue.trim();
    if (!name || name === financier.name) {
      setEditingName(false);
      return;
    }
    setBusy(true);
    const ok = await onRename(uuid, name);
    setBusy(false);
    if (ok) setEditingName(false);
  };

  const saveContact = async () => {
    setBusy(true);
    const ok = await onModifierContact(uuid, financier.name, {
      email: emailValue.trim() || null,
      emailsCc: emailsCcValue,
    });
    setBusy(false);
    if (ok) setEditingContact(false);
  };

  const cancelContact = () => {
    setEmailValue(financier.email || "");
    setEmailsCcValue(financier.emailsCc || []);
    setEditingContact(false);
  };

  const toggleAccordFinancement = async (checked) => {
    setBusyAccord(true);
    await onModifierAccordFinancement(uuid, financier.name, checked);
    setBusyAccord(false);
  };

  const handleDelete = () => {
    if (hasActiveEnveloppes) return;
    if (
      !window.confirm(
        `Supprimer le financeur « ${financier.name} » ? Cette action est définitive.`,
      )
    )
      return;
    onDelete(uuid);
  };

  const afterMutation = async (ok) => {
    if (ok) await onEnveloppesChanged();
    return ok;
  };

  return (
    <div className={styles.panel}>
      {/* ── En-tête ── */}
      <div className={styles.header}>
        <div className={styles.identity}>
          <div className={styles.icon}>{initiale(financier.name)}</div>
          {editingName ? (
            <div className={styles.nameRow}>
              <input
                className={styles.nameInput}
                value={nameValue}
                autoFocus
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveName();
                  if (e.key === "Escape") {
                    setNameValue(financier.name);
                    setEditingName(false);
                  }
                }}
              />
              <button
                type="button"
                className={styles.btnSavePill}
                onClick={saveName}
                disabled={busy || !nameValue.trim()}
              >
                <i className="fa fa-check" /> Enregistrer
              </button>
              <button
                type="button"
                className={styles.btnGhost}
                onClick={() => {
                  setNameValue(financier.name);
                  setEditingName(false);
                }}
              >
                Annuler
              </button>
            </div>
          ) : (
            <div className={styles.nameRow}>
              <h2 className={styles.name}>{financier.name}</h2>
              <button
                type="button"
                className={styles.editBtn}
                onClick={() => setEditingName(true)}
                title="Renommer le financeur"
                aria-label="Renommer le financeur"
              >
                <i className="fa fa-pen" />
              </button>
            </div>
          )}
        </div>
        {!editingName && (
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.btnDelete}
              onClick={handleDelete}
              disabled={hasActiveEnveloppes}
              title={
                hasActiveEnveloppes
                  ? "Clôturez d'abord ses enveloppes actives"
                  : "Supprimer ce financeur"
              }
              aria-label="Supprimer ce financeur"
            >
              <i className="fa fa-trash" />
            </button>
          </div>
        )}
      </div>

      {/* ── Contact (email par défaut / cc) — LS-625 ── */}
      <div className={styles.header}>
        {editingContact ? (
          <div className={styles.nameRow} style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
            <input
              className={styles.nameInput}
              type="email"
              value={emailValue}
              autoFocus
              placeholder="email@financeur.fr"
              onChange={(e) => setEmailValue(e.target.value)}
            />
            <TagInput
              name="emailsCc"
              label="Emails en copie"
              value={emailsCcValue}
              onChange={(e) => setEmailsCcValue(e.target.value)}
              placeholder="Ajouter un email en copie..."
            />
            <div>
              <button
                type="button"
                className={styles.btnSavePill}
                onClick={saveContact}
                disabled={busy}
              >
                <i className="fa fa-check" /> Enregistrer
              </button>
              <button
                type="button"
                className={styles.btnGhost}
                onClick={cancelContact}
                disabled={busy}
              >
                Annuler
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.nameRow}>
            <span>
              <i className="fa fa-envelope" style={{ marginRight: 6 }} />
              {financier.email || "Aucun email par défaut"}
              {financier.emailsCc?.length > 0 &&
                ` (cc : ${financier.emailsCc.join(", ")})`}
            </span>
            <button
              type="button"
              className={styles.editBtn}
              onClick={() => setEditingContact(true)}
              title="Modifier l'email de contact"
              aria-label="Modifier l'email de contact"
            >
              <i className="fa fa-pen" />
            </button>
          </div>
        )}
      </div>

      {/* ── Génération d'accord de financement (LS-666) ── */}
      <div className={styles.header}>
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id={`accord-financement-${uuid}`}
            checked={!!financier.peutGenererAccordFinancement}
            disabled={busyAccord}
            onChange={(e) => toggleAccordFinancement(e.target.checked)}
          />
          <label
            className="form-check-label"
            htmlFor={`accord-financement-${uuid}`}
          >
            Génération de l'accord de financement activée
          </label>
        </div>
      </div>

      {/* ── Bandeau capacité (lecture) ── */}
      {isLoading && enveloppes.length === 0 ? (
        <div className={styles.loaderWrap}>
          <Loader />
        </div>
      ) : (
        <>
          <div className={styles.bandHead}>Capacité des enveloppes actives</div>
          {hasActiveEnveloppes ? (
            <div className={styles.band}>
              {actives.map((env, index) => (
                <EnveloppeCard
                  key={env.uuid}
                  index={index}
                  consommation={{
                    financeur: { nom: env.nom || "Enveloppe" },
                    enveloppeOrigine:
                      env.consommation.enveloppeOrigine || env.montant,
                    reserve: env.consommation.reserve,
                    engage: env.consommation.engage,
                    disponible: env.consommation.disponible,
                  }}
                />
              ))}
            </div>
          ) : (
            <div className={styles.bandEmpty}>
              Aucune enveloppe active. Créez-en une ci-dessous.
            </div>
          )}

          {/* ── Gabarit par défaut du financeur ── */}
          <FinanceurConfigPanel
            financeurConfig={financeurConfig}
            onModifierEtapes={(etapes) =>
              modifierEtapesFinanceur(etapes).then(afterMutation)
            }
            onSynchroniserTodos={(todos) =>
              synchroniserTodosFinanceur(todos).then(afterMutation)
            }
          />

          {/* ── Gestion des enveloppes ── */}
          <EnveloppeTable
            enveloppes={enveloppes}
            isLoading={isLoading}
            onCreate={(form) => creer(form).then(afterMutation)}
            onUpdate={(envUuid, form) =>
              modifier(envUuid, form).then(afterMutation)
            }
            onClose={(envUuid) => cloturer(envUuid).then(afterMutation)}
            onModifierEtapes={(envUuid, etapes) =>
              modifierEtapes(envUuid, etapes).then(afterMutation)
            }
            onSynchroniserTodos={(envUuid, todos) =>
              synchroniserTodos(envUuid, todos).then(afterMutation)
            }
          />
        </>
      )}
    </div>
  );
};

export default FinanceurPanel;
